<template>
    <div class="login-form">
        <v-card class="popups-default">
            <v-card-title>
                <span class="headline">{{ $t('front.sign_in.login.titles.modal_title') }}</span>
            </v-card-title>

            <v-card-text class="loading-wrapper">
            <span class="preloader" :style="{backgroundImage: `url('${preloaderBackgroundImage}')`}"
                  v-if="loading"></span>
                <p>{{ $t('front.sign_in.login.titles.title') }}</p>
                <v-form
                    ref="form"
                    v-model="valid"
                    lazy-validation>
                    <div class="form-group">
                        <v-text-field type="email" v-model="username" :rules="usernameRules"
                                      :label="$t('front.sign_in.login.form.username')"
                                      required autocomplete="off" @change="errorMessage = null"/>
                    </div>

                    <div class="form-group">
                        <v-text-field type="password" v-model="password" :rules="passwordRules"
                                      :label="$t('front.sign_in.login.form.password')"
                                      required autocomplete="off" @change="errorMessage = null"/>
                    </div>

                    <div class="form-group" v-if="twoFactorAuth">
                        <v-text-field v-model="mfaCode" :rules="mfaCodeRules"
                                      :label="mfaCodeLabel"
                                      required autocomplete="off" @change="errorMessage = null"/>
                    </div>

                    <div class="form-group two-factor-auth" v-if="twoFactorAuth">
                        <span
                            v-html="$t('front.sign_in.login.form.can_resend_mfa_code_after', {minutes: waitUntilCanResetMinutes, seconds: waitUntilCanResetSeconds})"
                            v-if="canResendCodeAfterSeconds"/>

                        <v-btn density="compact" variant="plain"
                               v-text="$t('front.sign_in.login.form.resend_mfa_code')" @click="submitForm(true)"
                               :disabled="!!canResendCodeAfterSeconds"/>
                    </div>

                    <v-btn :disabled="!valid" @click="submitForm(false)">{{
                            $t('front.sign_in.login.button.login')
                        }}
                    </v-btn>
                </v-form>
                <v-card-actions>
                    <a href="javascript:void(0)" @click="forgot">{{ $t('front.sign_in.login.button.forgot') }}</a>
                    <a href="javascript:void(0)" @click="register"
                       v-if="signupEnabled">{{ $t('front.sign_in.login.button.create') }}</a>
                </v-card-actions>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import {createLoginData} from 'vendorModules/user/loginData'; // vendor depend webpack alias
import Toaster from "../../../../../src/mixins/toaster";
import apiData from "../../../../../src/mixins/apiData";
import {brandName, features} from "../../../../../config/app";
import {emailTwoFactorAuthChannel, smsTwoFactorAuthChannel} from "../../../../../src/interfaces/TwoFactorAuthInterface";
import {loginUser} from "../../../../../src/api";
import {routesNames} from "../../../../../src/interfaces/routeInterface";
import eventsInterface from "../../../../../src/interfaces/eventsInterface";

export default {
    name: "LoginForm",

    mixins: [Toaster, apiData],

    data: function () {
        return {
            loading: false,
            valid: true,
            username: undefined,
            usernameRules: [
                v => !!v || this.$t('front.validation.username.required'),
            ],
            password: undefined,
            passwordRules: [
                v => !!v || this.$t('front.validation.password.required'),
            ],
            mfaCode: undefined,
            mfaCodeRules: [
                v => !!v || this.$t('front.validation.mfa_code.required'),
            ],
            errorMessage: undefined,
            twoFactorAuth: false,
            twoFactorAuthNotificationChannel: undefined,
            canResendCodeAfterSeconds: 0,
            checkResendCodeInterval: null,
        };
    },

    mounted() {
        window.addEventListener('keyup', event => (parseInt(event.key) === 13 || event.keyCode === 13) && this.submitForm());

        void require('../../../../../src/util/analytics/index').trackEvent(eventsInterface.showLoginForm, this);
    },

    beforeDestroy() {
        if (this.checkResendCodeInterval) {
            clearInterval(this.checkResendCodeInterval);
            this.checkResendCodeInterval = null;
        }
    },

    computed: {
        preloaderBackgroundImage() {
            return `/public/${brandName}/spinners/sign-in-spinner.gif`;
        },

        signupEnabled() {
            return features.signup;
        },

        waitUntilCanResetMinutes() {
            return Math.floor(this.canResendCodeAfterSeconds / 60);
        },

        waitUntilCanResetSeconds() {
            return this.canResendCodeAfterSeconds % 60;
        },

        mfaCodeLabel() {
            if (this.twoFactorAuthNotificationChannel === emailTwoFactorAuthChannel) {
                return this.$t('front.sign_in.login.form.mfa_email_code');
            } else if (this.twoFactorAuthNotificationChannel === smsTwoFactorAuthChannel) {
                return this.$t('front.sign_in.login.form.mfa_sms_code');
            }
        },
    },

    methods: {
        register() {
            this.$router.push({
                name: routesNames.REGISTRATION_PAGE,
                params: {
                    locale: this.$store.state.locale,
                }
            });
        },

        forgot() {
            this.$store.commit('OPEN_FORGOT_PASSWORD_DIALOG');
        },

        submitForm(resendCode = false) {
            if (this.errorMessage) {
                this.makeErrorToast(this.errorMessage);
                return;
            }

            if (resendCode || this.$refs.form.validate()) {
                this.loading = true;

                let data = createLoginData(this.username, this.password);

                if (this.twoFactorAuth) {
                    data.mfaCode = this.mfaCode;
                }

                loginUser(this.createApiData(data))
                    .then(user => {
                        this.$store.commit('SET_USER', user);

                        let redirectPath = this.$store.getters.getLanguageUrlPrefix() || '/';
                        this.$router.push(redirectPath);
                    })
                    .catch(error => {
                        this.loading = false;

                        let status = error && error.response && error.response.status;

                        if (status === 449) {
                            this.twoFactorAuthNotificationChannel = error.response.data.responseData.notificationChannel;

                            if (this.twoFactorAuthNotificationChannel === emailTwoFactorAuthChannel) {
                                this.makeInfoToast(this.$t('front.sign_in.login.form.mfa_email_code_sent'));
                            } else if (this.twoFactorAuthNotificationChannel === smsTwoFactorAuthChannel) {
                                this.makeInfoToast(this.$t('front.sign_in.login.form.mfa_sms_code_sent'));
                            }

                            this.canResendCodeAfterSeconds = 180;
                            this.twoFactorAuth = true;

                            if (!this.checkResendCodeInterval) {
                                this.checkResendCodeInterval = setInterval(() => {
                                    if (this.canResendCodeAfterSeconds) {
                                        this.canResendCodeAfterSeconds--;
                                    } else {
                                        clearInterval(this.checkResendCodeInterval);
                                        this.checkResendCodeInterval = null;
                                    }
                                }, 1000);
                            }
                        } else if (status === 422) {
                            this.makeErrorToast(error && error.response && error.response.data && error.response.data.message || this.$t('front.sign_in.login.messages.service_unavailable'));
                        } else if (status === 424) {
                            this.makeErrorToast(error && error.response && error.response.data && error.response.data.message || this.$t('front.sign_in.login.messages.service_unavailable'));

                            this.$router.push({
                                name: routesNames.USER_RESET_PASSWORD,
                            });
                        } else {
                            this.makeErrorToast(this.$t('front.sign_in.login.messages.service_unavailable'))
                        }
                    });
            }
        },
    }
}
</script>

<style lang="sass">
@import "sass/components/signIn/LoginForm"
</style>